/* Cart Announcements Web Component
 * Handles fetch interception, cart announcements, and accessibility features
 * without polluting global scope
 */

// Track fetch interceptor initialization globally to prevent duplicates
let fetchInterceptorInitialized = false;

if (!customElements.get("cart-announcements")) {
  class CartAnnouncements extends HTMLElement {
    static #DEFAULT_TIMEOUT = 6000;

    /** @type {HTMLElement | null} */
    #announcementEl;
    /** @type {number | undefined} */
    #timeout;

    /** @type {MutationObserver | null} */
    #cartItemsObserver = null;
    /** @type {MutationObserver | null} */
    #cartToggleObserver = null;
    /** @type {MutationObserver | null} */
    #ariaLabelObserver = null;

    constructor() {
      super();

      // Initialize announcement element
      this.#announcementEl = this.querySelector("div");

      // Bind methods
      this.announceCartUpdate = this.announceCartUpdate.bind(this);
      this.handleCartAdd = this.handleCartAdd.bind(this);
      this.handleCartUpdate = this.handleCartUpdate.bind(this);
    }

    /**
     * Fetch Interceptor Setup
     * Initializes the fetch interceptor once to dispatch cart events
     */
    #setupFetchInterceptor() {
      // Only initialize once across all component instances
      if (fetchInterceptorInitialized) {
        return;
      }

      const { fetch: origFetch } = window;

      /**
       * Safely parse JSON from a cloned response
       * Returns null if response is not ok or JSON parsing fails
       * @param {Response} clonedResponse - The cloned response to parse
       * @returns {Promise<object | null>}
       */
      const safeParseJson = async (clonedResponse) => {
        // Only attempt JSON parsing for successful responses
        if (!clonedResponse.ok) {
          return null;
        }

        try {
          return await clonedResponse.json();
        } catch (error) {
          // JSON parsing failed (e.g., HTML error page, empty response)
          // Silently fail to maintain normal fetch behavior
          return null;
        }
      };

      window.fetch = async (...args) => {
        const response = await origFetch(...args);

        const url = args[0].toString();

        const clonedResp = response.clone();

        if (url.indexOf("/cart.js") !== -1) {
          const result = await safeParseJson(clonedResp);
          if (result) {
            document.dispatchEvent(
              new CustomEvent("on:cart:update", { detail: { cart: result } })
            );
          }
        }

        if (url.indexOf("/cart/add.js") !== -1) {
          const result = await safeParseJson(clonedResp);
          if (result) {
            document.dispatchEvent(
              new CustomEvent("on:cart:add", { detail: { cart: result } })
            );
          }
        }

        if (url.indexOf("/cart/clear.js") !== -1) {
          const result = await safeParseJson(clonedResp);
          if (result) {
            document.dispatchEvent(
              new CustomEvent("on:cart:clear", { detail: { cart: result } })
            );
          }
        }

        return response;
      };

      fetchInterceptorInitialized = true;
    }

    /**
     * Wait for Element
     * Utility method to wait for an element to appear in the DOM
     * @param {() => Element | null} selectorFn - Function that returns element when found
     * @returns {Promise<Element>}
     */
    #waitForElement(selectorFn) {
      return new Promise((resolve) => {
        const element = selectorFn();
        if (element) {
          return resolve(element);
        }

        const observer = new MutationObserver(() => {
          const element = selectorFn();
          if (element) {
            observer.disconnect();
            resolve(element);
          }
        });

        // If you get "parameter 1 is not of type 'Node'" error, see https://stackoverflow.com/a/77855838/492336
        observer.observe(document.body, {
          childList: true,
          subtree: true,
        });
      });
    }

    /**
     * Cart Toggle Watcher
     * Watches cart drawer open/close state and manages accessibility
     * @param {HTMLElement} cartEl - The cart element to watch
     */
    #watchCartToggle(cartEl) {
      const cartParentEl = cartEl.parentElement;
      if (!cartParentEl) return;

      const makeCartAccessible = () => {
        const isClosed = cartParentEl.classList.length === 0;

        cartParentEl.setAttribute("aria-hidden", isClosed ? "true" : "false");

        if (isClosed) {
          // Disconnect cart items observer when cart closes to prevent memory leak
          if (this.#cartItemsObserver) {
            this.#cartItemsObserver.disconnect();
            this.#cartItemsObserver = null;
          }
        } else {
          this.#waitForElement(() =>
            document.querySelector(
              "#cart-drawer-header .upcart-products-section"
            )
          ).then((cartItemsList) => {
            this.#makeRemovalButtonsAccessible(cartItemsList);
          });
        }
      };

      const handleMutations = () => {
        makeCartAccessible();
      };

      this.#cartToggleObserver = new MutationObserver(handleMutations);

      this.#cartToggleObserver.observe(cartParentEl, { attributes: true });
      makeCartAccessible();
    }

    /**
     * Make Removal Buttons Accessible
     * Adds screen reader accessible buttons for cart item removal
     * @param {Element} cartItemsList - The cart items list element
     */
    #makeRemovalButtonsAccessible(cartItemsList) {
      // If observer already exists for this element, skip creating a new one
      if (this.#cartItemsObserver) {
        return;
      }

      const makeRemoveButtonA11y = () => {
        cartItemsList
          .querySelectorAll('[class*="deleteButton"]')
          .forEach((deleteButton) => {
            if (
              !deleteButton.parentElement?.querySelector("button.patrol-sr-only")
            ) {
              const buttonA11y = /** @type {HTMLButtonElement} */ (
                document.createElement("button").cloneNode(false)
              );
              buttonA11y.classList.add("patrol-sr-only");
              buttonA11y.style.position = "absolute";
              buttonA11y.style.margin = "-1px";
              buttonA11y.style.border = "0";
              buttonA11y.style.padding = "0";
              buttonA11y.style.width = "1px";
              buttonA11y.style.height = "1px";
              buttonA11y.style.overflow = "hidden";
              buttonA11y.style.clip = "rect(0 0 0 0)";
              buttonA11y.style.whiteSpace = "nowrap";
              buttonA11y.setAttribute("aria-label", "Remove Item from Cart");
              buttonA11y.addEventListener("click", () => {
                if (deleteButton instanceof HTMLElement) {
                  deleteButton.click();
                }
              });
              deleteButton.insertAdjacentElement("afterend", buttonA11y);
            }
          });
      };

      const handleMutations = () => {
        makeRemoveButtonA11y();
      };

      // Store observer reference so it can be disconnected when cart closes
      this.#cartItemsObserver = new MutationObserver(handleMutations);

      this.#cartItemsObserver.observe(cartItemsList, { childList: true });

      makeRemoveButtonA11y();
    }

    /**
     * Add Shipping Aria Label
     * Adds aria-label to shipping info button for screen readers
     */
    #addShippingAriaLabel() {
      const addLabel = () => {
        const button = document.querySelector("button.corso-info-icon");
        if (button && !button.hasAttribute("aria-label")) {
          button.setAttribute(
            "aria-label",
            "more information about shipping"
          );
          return true;
        }
        return false;
      };

      // Try immediately in case it's already in the DOM
      if (!addLabel()) {
        this.#ariaLabelObserver = new MutationObserver((_, obs) => {
          if (addLabel()) {
            obs.disconnect();
            this.#ariaLabelObserver = null;
          }
        });

        this.#ariaLabelObserver.observe(document.body, {
          childList: true,
          subtree: true,
        });
      }
    }

    /**
     * Initialize Cart Drawer
     * Sets up cart drawer accessibility features
     */
    #initializeCartDrawer() {
      this.#waitForElement(() =>
        document.querySelector("#cart-drawer-header")
      ).then((cartEl) => {
        if (cartEl instanceof HTMLElement) {
          this.#watchCartToggle(cartEl);
          cartEl.insertAdjacentHTML(
            "afterbegin",
            '<cart-announcements><div id="cart-info" class="patrol-sr-only" style="position: absolute;   margin: -1px;   border: 0;   padding: 0;   width: 1px;   height: 1px;   overflow: hidden;   clip: rect(0 0 0 0);   white-space: nowrap;" role="status" aria-live="polite" aria-atomic="true"></div></cart-announcements>'
          );
        }
      });
    }

    /**
     * Announce Cart Update
     * Displays a message to screen readers
     * @param {string} msg - The message to announce
     */
    announceCartUpdate(msg) {
      if (this.#timeout) clearTimeout(this.#timeout);

      if (this.#announcementEl) {
        this.#announcementEl.innerHTML = msg;
      }

      this.#timeout = setTimeout(() => {
        if (this.#announcementEl) {
          this.#announcementEl.innerHTML = "";
        }
      }, CartAnnouncements.#DEFAULT_TIMEOUT);
    }

    /**
     * Handle Cart Add Event
     * Announces when items are added to cart
     * @param {CustomEvent} e - The cart add event
     */
    handleCartAdd(e) {
      const cart = e.detail.cart;

      // Handle direct items array (from cart add response)

      if (cart?.items && Array.isArray(cart.items)) {
        const items = cart.items;

        if (items.length === 1) {
          const item = items[0];

          const productName = item.product_title || item.title || "Product";

          this.announceCartUpdate(`${productName} added to cart`);
        } else {
          this.announceCartUpdate(`${items.length} items added to cart`);
        }

        return;
      }

      // Handle single item objects (legacy format)

      if (cart?.product_title || cart?.title) {
        const productName = cart.product_title || cart.title || "Product";

        this.announceCartUpdate(`${productName} added to cart`);

        return;
      }

      // Fallback announcement

      this.announceCartUpdate("Item added to cart");
    }

    /**
     * Handle Cart Update Event
     * Announces when cart is updated
     * @param {CustomEvent} e - The cart update event
     */
    handleCartUpdate(e) {
      const cart = e.detail.cart;

      if (cart && cart.item_count !== undefined) {
        const itemCount = cart.item_count;

        if (itemCount === 0) {
          this.announceCartUpdate("Cart is now empty");
        } else {
          this.announceCartUpdate(
            `Cart updated. ${itemCount} item${itemCount !== 1 ? "s" : ""} in cart`
          );
        }
      }
    }

    /**
     * Connected Callback
     * Initializes component when added to DOM
     */
    connectedCallback() {
      // Guard: Prevent nested instances from re-initializing
      // Check if we're inside another cart-announcements element
      if (this.parentElement?.closest("cart-announcements")) {
        return;
      }

      // Guard: Check if announcement element already exists
      if (document.querySelector("#cart-info")) {
        return;
      }

      // Initialize fetch interceptor (only once across all instances)
      this.#setupFetchInterceptor();

      // Set up cart event listeners
      document.addEventListener("on:cart:add", this.handleCartAdd);
      document.addEventListener("on:cart:update", this.handleCartUpdate);

      // Initialize accessibility features
      this.#initializeCartDrawer();
      this.#addShippingAriaLabel();
    }

    /**
     * Disconnected Callback
     * Cleanup when component is removed from DOM
     */
    disconnectedCallback() {
      // Remove cart event listeners
      document.removeEventListener("on:cart:add", this.handleCartAdd);
      document.removeEventListener("on:cart:update", this.handleCartUpdate);

      // Disconnect all observers
      if (this.#cartItemsObserver) {
        this.#cartItemsObserver.disconnect();
        this.#cartItemsObserver = null;
      }

      if (this.#cartToggleObserver) {
        this.#cartToggleObserver.disconnect();
        this.#cartToggleObserver = null;
      }

      if (this.#ariaLabelObserver) {
        this.#ariaLabelObserver.disconnect();
        this.#ariaLabelObserver = null;
      }

      // Clear announcement timeout
      if (this.#timeout) clearTimeout(this.#timeout);
    }
  }

  customElements.define("cart-announcements", CartAnnouncements);
}
